import React, { useState } from 'react'
import { AdCreative, LeadCard } from '@marketing-milk/interfaces'
import { Autocomplete, TextField } from '@mui/material'
import { BlankPageLayout } from 'components/BlankPageLayout'
import { ConfirmationModal } from 'components/ConfirmationModal'
import { LeadFormChip } from './LeadFormChip'
import { useLeadCardCandidates } from './useLeadCardCandidates'

type Props = {
  businessID: number
  campaignName?: string
  creatives: AdCreative[]
  declaredForms: LeadCard[]
  setDeclaredForms: (forms: LeadCard[]) => void
  onUnassignForm: (form: LeadCard) => Promise<void>
}

export const DeclareLeadFormsStep = ({
  businessID,
  campaignName,
  creatives,
  declaredForms,
  setDeclaredForms,
  onUnassignForm,
}: Props) => {
  const { leadCards, truncated } = useLeadCardCandidates(businessID)
  const [pendingRemoval, setPendingRemoval] = useState<LeadCard>()
  const [searchInput, setSearchInput] = useState('')

  const countForForm = (form: LeadCard) =>
    creatives.filter(c => c.leadCardUsed?.id === form.id).length

  const removeForm = (form: LeadCard) => {
    if (countForForm(form) > 0) {
      setPendingRemoval(form)
      return
    }
    setDeclaredForms(declaredForms.filter(f => f.id !== form.id))
  }

  // Already-declared forms are excluded from the search results — picking one again would just
  // be a confusing no-op — and shown as their own row above the box instead of living inside it,
  // so the input's height and label position stay put as the batch grows.
  const availableOptions = (leadCards ?? []).filter(
    form => !declaredForms.some(declared => declared.id === form.id)
  )

  return (
    <BlankPageLayout
      className="max-w-3xl"
      title={
        campaignName
          ? `Declare This Batch's Lead Forms — ${campaignName}`
          : "Declare This Batch's Lead Forms"
      }
      subtitle="Search for and add every lead form this batch of ads uses. The next step assigns ads to these forms."
    >
      {declaredForms.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3 w-full">
          {declaredForms.map(form => (
            <LeadFormChip key={form.id} form={form} onRemove={() => removeForm(form)} />
          ))}
        </div>
      )}

      <Autocomplete
        fullWidth
        sx={{ minWidth: 420 }}
        options={availableOptions}
        value={null}
        inputValue={searchInput}
        onInputChange={(_, value) => setSearchInput(value)}
        onChange={(_, selected) => {
          if (!selected) return
          setDeclaredForms([...declaredForms, selected])
          setSearchInput('')
        }}
        getOptionLabel={form => form.formName}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            label="Search lead forms"
            placeholder="Type a form name…"
          />
        )}
      />

      {truncated && (
        <p className="text-sm text-gray-500 mt-2">
          Showing the 150 most recently created forms — search by exact name if yours isn't listed
          here.
        </p>
      )}

      <ConfirmationModal
        openState={[!!pendingRemoval, open => !open && setPendingRemoval(undefined)]}
        warningText={
          pendingRemoval &&
          `Removing this form will unassign ${countForForm(pendingRemoval)} ad${
            countForForm(pendingRemoval) === 1 ? '' : 's'
          } — everywhere, not just this campaign.`
        }
        confirmButtonText="Remove Form"
        confirmButtonType="delete"
        onConfirm={async () => {
          if (!pendingRemoval) return
          const removed = pendingRemoval
          await onUnassignForm(removed)
          setDeclaredForms(declaredForms.filter(f => f.id !== removed.id))
          setPendingRemoval(undefined)
        }}
      />
    </BlankPageLayout>
  )
}
